import React, { createContext, useCallback, useContext, useRef, useState } from 'react';

// Toast notifications — the kit's answer to "how do I confirm an action
// without an alert()". Dependency-free provider + hook:
//
//   const toast = useToast();
//   toast('Saved');                        // neutral
//   toast('Link copied', 'ok');           // ok | info | warn | danger
//   toast('Upload failed', 'danger', 8000);
//
// Announced via a polite live region; auto-dismisses (default 4s) with a
// manual close. Styles in styles/components.css (.cs-toast*).

const ToastContext = createContext(() => {});

export function useToast() {
  return useContext(ToastContext);
}

const EXIT_MS = 160; // must match csToastOut duration

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id) => {
    // Two-phase removal so the exit animation plays.
    setToasts((ts) => ts.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
    setTimeout(() => {
      setToasts((ts) => ts.filter((t) => t.id !== id));
    }, EXIT_MS);
  }, []);

  // Optional 4th arg: { label, onClick } renders an inline action ("Undo").
  const push = useCallback((message, variant = '', durationMs = 4000, action = null) => {
    const id = ++idRef.current;
    setToasts((ts) => [...ts.slice(-3), { id, message, variant, action }]);
    if (durationMs > 0) setTimeout(() => dismiss(id), durationMs);
    return id;
  }, [dismiss]);

  return (
    <ToastContext.Provider value={push}>
      {children}
      <div className="cs-toast-stack" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`cs-toast${t.variant ? ` t-${t.variant}` : ''}${t.leaving ? ' leaving' : ''}`}
          >
            <span className="cs-toast-msg">{t.message}</span>
            {t.action && (
              <button
                type="button"
                className="cs-btn cs-btn-ghost cs-btn-sm"
                onClick={() => { dismiss(t.id); t.action.onClick(); }}
              >
                {t.action.label}
              </button>
            )}
            <button
              type="button"
              className="cs-toast-close"
              aria-label="Dismiss notification"
              onClick={() => dismiss(t.id)}
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
